<template>
  <default-textual-section v-if="$store.state.displayType === 'textual'" :menu="menu" :section="section"
                           menu-item-component="pancho-willies-food-item"
                           class="menu--section-pancho__willies"></default-textual-section>
  <default-visual-section v-else :menu="menu" :section="section"
                          menu-item-component="pancho-willies-food-item"
                          class="menu--section-pancho__willies"></default-visual-section>
</template>

<script lang="ts">
/**
 * Custom component for Bimini default menu sections.
 *
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 2.0.0
 * @copyright  Copyright (C) 2023-2025, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 *  2023-06-07 (1.0.0) (jhoward)
 *    - Copy from Bimini_defaultSection.vue component to PanchoWillies_defaultSection.vue component.
 *
 *  2023-06-21 (1.0.1) (jhoward)
 *    - Give PanchoWillies_defaultSection.vue a default background color.
 *
 *  2023-07-26 (1.0.2) (jhoward)
 *    - Fix over-generic CSS selectors in PanchoWillies_defaultSection.vue.
 *
 *  2023-08-23 (1.1.0) (jhoward)
 *    - Use <menu-version> in <PanchoWillies_defaultSection>.
 *
 *  2024-07-19 (1.1.1) (jhoward)
 *    - Rename .visual_menu--tag CSS rules to .menu--tag in <pancho-willies-default-section>.
 *
 *  2025-02-10 (1.2.0) (jhoward)
 *    - Add <menu-schedules> to the <pancho-willies-default-section> component if in admin view.
 *
 *  2025-04-08 (2.0.0) (jhoward)
 *    - Use <default-textual-section> and <default-visual-section> in <PanchoWillies_defaultSection>.
 *    - Add textual and visual menu CSS tweaks to <PanchoWillies_defaultSection>.
 */

import {defineComponent, PropType} from "vue";
import {Menu, MENU_ITEM_TAG_LOCATIONS, MenuSection} from "../defs";
import DefaultVisualSection from "./visual/defaultVisualSection.vue";
import DefaultTextualSection from "./textual/defaultTextualSection.vue";

export default defineComponent({
  components: {
    DefaultTextualSection, DefaultVisualSection,
  },
  name: "PanchoWillies_defaultSection",
  props: {
    section: {
      type: Object as PropType<MenuSection>,
      required: true
    },
    menu: {
      type: Object as PropType<Menu>,
      required: true
    },
  },
})
</script>

<style>
.textual_menu--container-pancho__willies h1 {
  display: none;
}

.textual_menu--container-pancho__willies .textual_menu--section h1 {
  display: initial;
}

html .visual_menu--container-pancho__willies {
  background-color: #e3dbce;
}

html .textual_menu--container-pancho__willies {
  background-color: #faf5e8;
}

.visual_menu--container-pancho__willies .visual_menu--page {
  background-image: url('/library/images/menu/background-pancho-willies-specialty-drinks.jpg');
  background-size: cover;
}

.visual_menu--container-pancho__willies .visual_menu--page.visual_menu--content_top {
  background-image: url('/library/images/menu/background-pancho-willies.jpg');
  background-size: cover;
}

.visual_menu--container-pancho__willies .visual_menu--content_top {
  height: 80vh;
  text-align: center;
}

.textual_menu--container-pancho__willies .page-link-group-copy-top {
  text-align: center;
}

.visual_menu--container-pancho__willies .visual_menu--content_bottom .visual_menu--section,
.textual_menu--container-pancho__willies .body-container .page-link-group-copy-bottom .viewport {
  padding: 0;
}

.visual_menu--container-pancho__willies .pancho_willies--logo,
.textual_menu--container-pancho__willies .pancho_willies--logo {
  width: 100%;
}
</style>

<style scoped>
html .menu--section.menu--section-pancho__willies {
  margin: 0 auto;
  padding-top: 0.5em;
  max-width: 748px;
}

.visual_menu--section.menu--section-pancho__willies {
  font-size: 0.8em;
}

.visual_menu--container-landscape .menu--section-pancho__willies {
  padding-top: 0.5em;
}

.textual_menu--section.menu--section-pancho__willies:deep(.menu--section_title) {
  max-width: calc(748px - 4rem);
  margin-left: auto;
  margin-right: auto;
}

html .menu--section-pancho__willies:deep(.menu--section_subtitle) {
  text-align: left;
  margin-top: -1.5em;
  margin-bottom: 1em;
  color: #000000;
  letter-spacing: initial;
}

.menu--section-pancho__willies:deep(.menu--section_title) {
  color: #000000;
  margin: 2rem 0 0.9em 0;
  padding: 0;
  position: relative;
  overflow: initial;
  font-family: alverata, serif;
  font-weight: 700;
  font-style: normal;
  text-transform: capitalize;
  letter-spacing: 0.05em;
  font-size: 1.5em;
  text-align: left;
}

.menu--section-pancho__willies:deep(.menu--section_subtitle) {
  margin-top: -1em;
  font-size: 1em;
  font-weight: 400;
  margin-bottom: 0.4em;
  text-align: center;
  color: #3e2603;
}

.menu--section-pancho__willies:deep(.menu--section_subgroup_title) {
  color: #000000;
}

.menu--section-pancho__willies.menu--section-show_pricing_header >>> .menu--section_subgroup_title span:not(:first-child) {
  text-decoration: underline;
}

.menu--section-pancho__willies:deep(.menu--item_title) {
  font-weight: 700;
}

.menu--section-pancho__willies:deep(.menu--section-highlight) {
  background-color: #f7f3eb;
}

.menu--section-pancho__willies:deep(.menu--item-text_only) {
  color: #3e2603;
  font-size: 0.8em;
  margin-top: 1em;
  margin-bottom: 1em;
}

.menu--section-pancho__willies:deep(.menu--item-highlighted_box) {
  color: #3e2603;
}

.menu--section-pancho__willies:deep(.menu--item-highlighted_box) {
  position: relative;
  border-color: #3e2603;
  max-width: 75%;
  margin-left: auto;
  margin-right: auto;
}

@media (prefers-contrast: more) {
  .menu--section-pancho__willies:deep(.menu--section_subtitle),
  .menu--section-pancho__willies:deep(.menu--item-text_only),
  .menu--section-pancho__willies:deep(.menu--item-highlighted_box) {
    color: #000;
  }

  .menu--section-pancho__willies:deep(.menu--item-highlighted_box) {
    border-color: #000;
  }
}

.menu--section-pancho__willies:deep(.menu--item-highlighted_box .menu--item_description) {
  font-size: 0.9em;
  line-height: 1.2em;
}

.menu--section-pancho__willies:deep(.menu--tag) {
  font-size: 2em;
  margin-right: 0.3em;
  top: -0.2em;
}

.menu--section-pancho__willies:deep(.menu-tag-legend .menu--tag) {
  font-size: inherit;
  margin-right: inherit;
  top: inherit;
}

.menu--section-pancho__willies:deep(.menu--tag::after) {
  margin-left: 3em;
}

.menu--section-pancho__willies:deep(.menu-tag-legend .menu--tag::after) {
  margin-left: inherit;
}
</style>